import { useCallback, useEffect, useMemo, useState } from "react";
import { useInboxConversation, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { htmlToText, withKey } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useDraftAutosave, useDraftMutations, useInboxThreadDraft, type DraftCreateFields } from "../../hooks";
import { useInboxMessageDeleteMutations } from "../../../../hooks";
import { pendo } from "../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../components/MessageCompositionFooter";
import { DraftAutosaveIndicator } from "../../../../components/DraftAutosaveIndicator";
import { buildHtmlMessageWithFiles } from "../../utils";
import { EmailReplyModeControl, EmailReplyMode } from "./EmailReplyModeControl";

export interface InboxThreadEmailCompositionFooterProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxThreadEmailCompositionFooter = withKey(
  ({ threadId, conversationData }: InboxThreadEmailCompositionFooterProps) => {
    const { t } = useTranslation("inbox");
    const { deleteEmailMutation } = useInboxMessageDeleteMutations();
    const [replyMode, setReplyMode] = useState<EmailReplyMode>("reply-all");
    const [composerKey, setComposerKey] = useState(0);

    const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
    const resolvedConversationData = conversationData ?? internalConversationData;
    const { messages, endusers } = resolvedConversationData;
    const metadata = resolvedConversationData.metadata;
    const enduserId = endusers.at(0)?.id;
    const latestMessage = messages.at(0);

    const {
      queryParams,
      setQueryParams,
      matchingDraft,
      isEditingScheduled,
      sendAtInPast,
      shouldQueryDraft,
      autoSelectedDraft,
    } = useInboxThreadDraft({ conversationData: resolvedConversationData });

    const draftQuery = useTellescopeQuery({
      model: "emails",
      method: "getOne",
      request: queryParams.draftId ?? "",
      enabled: shouldQueryDraft,
    });

    const latestDraft = useMemo(() => {
      if (queryParams.draftId) {
        return matchingDraft?.content ?? draftQuery.data?.HTMLContent;
      }
      return autoSelectedDraft?.content;
    }, [autoSelectedDraft, draftQuery.data, matchingDraft, queryParams.draftId]);

    const autosaveDraftId = queryParams.draftId ? matchingDraft?.id ?? queryParams.draftId : autoSelectedDraft?.id;

    const buildDraftFields = useCallback((): DraftCreateFields | undefined => {
      if (!enduserId || !metadata?.title) return undefined;
      return {
        channel: "Email",
        enduserId,
        subject: `Re: ${metadata.title}`,
        isMarketing: false,
        inbound: false,
        ...(replyMode === "reply-all" && latestMessage?.cc ? { cc: latestMessage.cc } : {}),
        ...(latestMessage?.emailMessageId ? { replyTo: latestMessage.emailMessageId } : {}),
      };
    }, [enduserId, latestMessage?.cc, latestMessage?.emailMessageId, metadata?.title, replyMode]);

    const autosave = useDraftAutosave({
      loadedContent: latestDraft,
      draftId: autosaveDraftId,
      isEditingScheduled,
      buildCreateFields: buildDraftFields,
    });

    const { sendDraft: sendDraftMutation } = useDraftMutations();

    const flushAutosave = autosave.flush;
    useEffect(() => flushAutosave, [flushAutosave]);

    const sendEmailMutation = useTellescopeMutation({
      model: "emails",
      method: "createOne",
      onSuccess: () => {
        setComposerKey((k) => k + 1);
        setQueryParams((prev) => ({ ...prev, draftId: undefined }));
      },
    });

    const isMutating = sendEmailMutation.isPending || deleteEmailMutation.isPending;
    const canSend = !!enduserId && !!metadata?.title && !isMutating;

    const buildPayload = (
      { message, files }: MessageCompositionFooterPayload,
      resolvedEnduserId: string,
      overrides?: { sendAt?: Date }
    ) => {
      const cc = replyMode === "reply-all" && latestMessage?.cc ? latestMessage.cc : undefined;
      const html = buildHtmlMessageWithFiles(message, files);
      return {
        replyTo: latestMessage?.emailMessageId,
        enduserId: resolvedEnduserId,
        isMarketing: false,
        HTMLContent: html,
        textContent: htmlToText(html),
        subject: `Re: ${metadata?.title}`,
        inbound: false,
        ...(cc && { cc }),
        ...overrides,
      };
    };

    return (
      <MessageCompositionFooter
        key={composerKey}
        editorVariant="richtext"
        channelType="email"
        placeholder={t("Write a reply...")}
        submitOnEnter={false}
        initialMessage={latestDraft}
        canSend={canSend}
        isUpdatePending={isMutating}
        enduserId={enduserId}
        isEditingScheduled={isEditingScheduled}
        sendAtInPast={sendAtInPast}
        initDate={matchingDraft?.sendAt}
        scheduleDialogKey={matchingDraft?.sendAt?.toISOString() ?? "no-init-date"}
        onMessageChange={autosave.onMessageChange}
        leadingActions={<DraftAutosaveIndicator status={autosave.status} />}
        trailingActions={
          !isEditingScheduled && (
            <EmailReplyModeControl
              replyMode={replyMode}
              onChangeReplyMode={setReplyMode}
              forwardEmailId={latestMessage?.id}
              disabled={sendEmailMutation.isPending}
            />
          )
        }
        onSubmit={(payload) => {
          if (!enduserId || !metadata?.title) return;
          pendo.track("messaging-email-thread-compose-send");
          const draftId = autosave.takeDraftForSend();
          const draftFields = buildDraftFields();

          if (draftId && draftFields) {
            sendDraftMutation({
              id: draftId,
              message: buildHtmlMessageWithFiles(payload.message, payload.files),
              fields: draftFields,
            }).then(() => {
              setComposerKey((k) => k + 1);
              setQueryParams((prev) => ({ ...prev, draftId: undefined }));
            });
            return;
          }
          sendEmailMutation.mutate(buildPayload(payload, enduserId));
        }}
        onSchedule={(payload, sendAt) => {
          if (!enduserId || !metadata?.title) return;
          if (matchingDraft) {
            const draftId = matchingDraft.id;
            deleteEmailMutation.mutate(draftId, {
              onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt })),
            });
          } else {
            sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt }));
          }
        }}
        onSendNow={(payload) => {
          if (!matchingDraft || !enduserId || !metadata?.title) return;
          const draftId = matchingDraft.id;
          deleteEmailMutation.mutate(draftId, {
            onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt: new Date() })),
          });
        }}
        onSaveScheduled={(payload) => {
          if (!matchingDraft || !enduserId || !metadata?.title) return;
          const draft = matchingDraft;
          deleteEmailMutation.mutate(draft.id, {
            onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt: draft.sendAt })),
          });
        }}
        onReschedule={(payload, sendAt) => {
          if (!matchingDraft || !enduserId || !metadata?.title) return;
          const draftId = matchingDraft.id;
          deleteEmailMutation.mutate(draftId, {
            onSuccess: () => sendEmailMutation.mutate(buildPayload(payload, enduserId, { sendAt })),
          });
        }}
        trackingIds={{
          schedule: "email-thread-schedule-message-dialog-open",
          reschedule: "email-thread-reschedule-message-dialog-open",
        }}
      />
    );
  },
  "threadId"
);
